// src/pages/mairie/ListeRecenses.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function ListeRecenses() {
    const [recenses, setRecenses] = useState([]);
    const [statsRecenseurs, setStatsRecenseurs] = useState([]);
    const [totalGeneral, setTotalGeneral] = useState(0);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(null);
    const [search, setSearch] = useState('');

    const fetchListe = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/liste_recenses.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setRecenses(data.recenses);
                setStatsRecenseurs(data.stats_recenseurs);
                setTotalGeneral(data.total_general);
            }
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchListe();
    }, []);

    const genererPDF = async (id) => {
        setGenerating(id);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${config.apiBase}/mairie/fiche_pdf.php?id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                const win = window.open('', '_blank');
                win.document.write(data.html);
                win.document.close();
                win.print();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setGenerating(null);
        }
    };

    const filteredRecenses = recenses.filter(r => {
        const searchTerm = search.toLowerCase();
        return (
            r.nom?.toLowerCase().includes(searchTerm) ||
            r.prenom?.toLowerCase().includes(searchTerm) ||
            r.numero_fiche?.toLowerCase().includes(searchTerm) ||
            r.telephone?.includes(searchTerm)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6">📋 Liste des recensés</h1>

            {/* Recherche */}
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="🔍 Rechercher par nom, prénom, numéro de fiche ou téléphone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Statistiques par recenseur */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {statsRecenseurs.map(s => (
                    <div key={s.id_recenseur} className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-600">
                        <p className="text-sm text-gray-500">{s.prenom} {s.nom}</p>
                        <p className="text-2xl font-bold">{s.total}</p>
                        <p className="text-xs text-gray-400">recensés</p>
                    </div>
                ))}
                <div className="bg-blue-600 rounded-xl shadow p-4 text-white">
                    <p className="text-sm text-blue-200">Total général</p>
                    <p className="text-2xl font-bold">{totalGeneral}</p>
                    <p className="text-xs text-blue-200">recensés</p>
                </div>
            </div>

            {/* Tableau */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="overflow-x-auto">
                    {filteredRecenses.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            Aucun résultat trouvé
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Fiche</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prénom</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quartier</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recenseur</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredRecenses.map(r => (
                                    <tr key={r.id_contribuable} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-mono">{r.numero_fiche}</td>
                                        <td className="px-4 py-3 text-sm">{r.nom}</td>
                                        <td className="px-4 py-3 text-sm">{r.prenom}</td>
                                        <td className="px-4 py-3 text-sm">{r.telephone}</td>
                                        <td className="px-4 py-3 text-sm">{r.quartier || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{r.recenseur_prenom || ''} {r.recenseur_nom || ''}</td>
                                        <td className="px-4 py-3 text-center">
                                            <button
                                                onClick={() => genererPDF(r.id_contribuable)}
                                                disabled={generating === r.id_contribuable}
                                                className="bg-blue-600 text-white px-4 py-1 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1 mx-auto"
                                            >
                                                {generating === r.id_contribuable ? '...' : '📄 Voir la fiche'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500">
                    Total: {filteredRecenses.length} contribuable(s)
                </div>
            </div>
        </div>
    );
}